import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "@softmax/observatory-client/client";
import type { Schemas } from "@softmax/observatory-client/types";
type FeedbackViewerStatus = Schemas["FeedbackViewerStatus"];
import { useDiscardUnavailableData } from "../../data/use-discard-unavailable-data";
import { useBrowserSession } from "../../session/use-browser-session";

export function useFeedbackViewer({
  isDisabledPath,
}: {
  readonly isDisabledPath: boolean;
}) {
  const { session, kind } = useBrowserSession();
  const client = useQueryClient();
  const viewerKey = ["feedback", "viewer", session?.user.id ?? "anonymous"];
  const query = useQuery({
    queryKey: viewerKey,
    queryFn: ({ signal }) =>
      apiGet<FeedbackViewerStatus>("/v2/feedback/viewer", {
        elevatedPrivileges: true,
        signal,
      }),
    enabled: !isDisabledPath && kind !== "pending" && session !== null,
    staleTime: 5 * 60000,
  });
  const unavailable = useDiscardUnavailableData({
    queryKey: viewerKey,
    error: query.error,
  });
  const revokeLauncher = () =>
    client.setQueryData<FeedbackViewerStatus>(viewerKey, (value) =>
      value ? { ...value, launcherEnabled: false } : value,
    );
  return {
    launcherEnabled: !unavailable && query.data?.launcherEnabled === true,
    revokeLauncher,
  };
}
